import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface CampaignRow {
  id: string;
  name: string;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  failed: number;
  deliveryRate: number;
  readRate: number;
  replyRate: number;
  conversionRate: number;
}

interface DailyStat {
  date: string;
  sent: number;
  delivered: number;
  read: number;
}

interface RevenueMonth {
  month: string;
  revenue: number;
  count: number;
}

interface ReportSummary {
  totalCampaigns: number;
  activeCampaigns: number;
  totalMessages: number;
  deliveredRate: number;
  readRate: number;
  replyRate: number;
  totalRevenue: number;
  totalClients: number;
  activeClients: number;
  trialClients: number;
  conversionRate: number;
}

const emptySummary: ReportSummary = {
  totalCampaigns: 0, activeCampaigns: 0, totalMessages: 0,
  deliveredRate: 0, readRate: 0, replyRate: 0,
  totalRevenue: 0, totalClients: 0, activeClients: 0,
  trialClients: 0, conversionRate: 0,
};

const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
function formatRate(n: number) { return `${n.toFixed(1)}%`; }
function rateColor(n: number) { return n >= 80 ? 'text-emerald-600' : n >= 50 ? 'text-amber-600' : 'text-red-500'; }
function formatNumber(n: number) { return n.toLocaleString('pt-BR'); }
function formatCurrency(n: number) { return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }

const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function ReportsPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id;

  const [summary, setSummary] = useState<ReportSummary>(emptySummary);
  const [campaignRows, setCampaignRows] = useState<CampaignRow[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [revenueMonths, setRevenueMonths] = useState<RevenueMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState('');
  const [exportError, setExportError] = useState('');
  const [activeTab, setActiveTab] = useState<'performance' | 'revenue' | 'clients' | 'conversion'>('performance');

  const fetchReports = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    try {
      // Fetch all campaigns for this company
      const { data: campaigns, error: fetchErr } = await supabase
        .from('campaigns')
        .select('id, name, status, sent_count, delivered_count, read_count, replied_count, failed_count, created_at')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (fetchErr) throw new Error(fetchErr.message);

      const campData = campaigns || [];
      const totalCampaigns = campData.length;
      const activeCampaigns = campData.filter((c) => c.status === 'ativa' || c.status === 'active').length;
      const totalMessages = campData.reduce((sum, c) => sum + (c.sent_count || 0), 0);
      const totalDelivered = campData.reduce((sum, c) => sum + (c.delivered_count || 0), 0);
      const totalRead = campData.reduce((sum, c) => sum + (c.read_count || 0), 0);
      const totalReplied = campData.reduce((sum, c) => sum + (c.replied_count || 0), 0);

      // Fetch payments for this company's users
      const { data: payments } = await supabase
        .from('payments')
        .select('amount, status, created_at')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      const approvedPayments = payments || [];
      const totalRevenue = approvedPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

      // Fetch profiles for client stats
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, plan, created_at, company_id');

      const allProfiles = profiles || [];
      const companyProfiles = allProfiles.filter((p) => p.company_id === companyId);
      const activeClients = allProfiles.filter((p) => p.plan !== 'trial' && p.plan !== 'free').length;
      const trialClients = allProfiles.filter((p) => p.plan === 'trial').length;
      const totalClients = allProfiles.length;

      const convertedClients = allProfiles.filter((p) => p.plan !== 'trial' && p.plan !== 'free').length;
      const conversionRate = totalClients > 0 ? Math.round((convertedClients / totalClients) * 1000) / 10 : 0;

      setSummary({
        totalCampaigns, activeCampaigns, totalMessages,
        deliveredRate: totalMessages > 0 ? Math.round((totalDelivered / totalMessages) * 1000) / 10 : 0,
        readRate: totalDelivered > 0 ? Math.round((totalRead / totalDelivered) * 1000) / 10 : 0,
        replyRate: totalDelivered > 0 ? Math.round((totalReplied / totalDelivered) * 1000) / 10 : 0,
        totalRevenue, totalClients, activeClients, trialClients, conversionRate,
      });

      // Build campaign rows
      const rows: CampaignRow[] = campData.map((c) => {
        const sent = c.sent_count || 0;
        const delivered = c.delivered_count || 0;
        return {
          id: c.id, name: c.name, sent, delivered,
          read: c.read_count || 0, replied: c.replied_count || 0, failed: c.failed_count || 0,
          deliveryRate: sent > 0 ? Math.round((delivered / sent) * 1000) / 10 : 0,
          readRate: delivered > 0 ? Math.round(((c.read_count || 0) / delivered) * 1000) / 10 : 0,
          replyRate: delivered > 0 ? Math.round(((c.replied_count || 0) / delivered) * 1000) / 10 : 0,
          conversionRate: delivered > 0 ? Math.round(((c.replied_count || 0) / delivered) * 1000) / 10 : 0,
        };
      });
      setCampaignRows(rows);

      // Daily stats from queue_jobs
      if (campData.length > 0) {
        const campaignIds = campData.map((c) => c.id);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
        sevenDaysAgo.setHours(0, 0, 0, 0);

        const { data: jobs } = await supabase
          .from('queue_jobs')
          .select('status, processed_at')
          .in('campaign_id', campaignIds)
          .gte('processed_at', sevenDaysAgo.toISOString());

        const dailyMap: Record<string, { sent: number; delivered: number; read: number }> = {};
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          const key = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          dailyMap[key] = { sent: 0, delivered: 0, read: 0 };
        }

        (jobs || []).forEach((job) => {
          if (!job.processed_at) return;
          const d = new Date(job.processed_at);
          const key = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          if (!dailyMap[key]) return;
          dailyMap[key].sent += 1;
          if (job.status === 'delivered' || job.status === 'sent') dailyMap[key].delivered += 1;
          if (job.status === 'read') dailyMap[key].read += 1;
        });

        setDailyStats(Object.entries(dailyMap).map(([date, vals]) => ({ date, ...vals })));
      } else {
        setDailyStats([]);
      }

      // Revenue by month (last 12 months)
      const revMap: Record<string, { revenue: number; count: number }> = {};
      for (let i = 11; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const key = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
        revMap[key] = { revenue: 0, count: 0 };
      }

      approvedPayments.forEach((p) => {
        if (!p.created_at) return;
        const d = new Date(p.created_at);
        const key = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
        if (!revMap[key]) return;
        revMap[key].revenue += Number(p.amount) || 0;
        revMap[key].count += 1;
      });

      setRevenueMonths(Object.entries(revMap).map(([month, vals]) => ({ month, ...vals })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleExportCSV = async () => {
    setExportLoading(true);
    setExportError('');
    setExportSuccess('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('export-report', {
        body: { format: 'csv', campaign_id: selectedCampaign || undefined },
      });
      if (fnErr) { setExportError(fnErr.message); setExportLoading(false); return; }
      if (!data?.success) { setExportError(data?.message || 'Sem dados'); setExportLoading(false); return; }

      if (data.campaigns) {
        const headers = ['Nome', 'Status', 'Total Contatos', 'Enviadas', 'Entregues', 'Lidas', 'Respondidas', 'Falhas', 'Tx. Entrega', 'Tx. Leitura', 'Criada em'];
        const rows = data.campaigns.map((c: Record<string, unknown>) =>
          [c.name, c.status, c.total_contacts, c.sent_count, c.delivered_count, c.read_count, c.replied_count, c.failed_count, c.delivery_rate + '%', c.read_rate + '%', c.created_at]
            .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
        );
        const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio-campanhas-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setExportSuccess('CSV exportado com sucesso!');
      setTimeout(() => setExportSuccess(''), 3000);
    } catch (err) {
      setExportError('Erro: ' + (err instanceof Error ? err.message : 'Desconhecido'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportExcel = () => {
    const headers = ['Campanha', 'Enviadas', 'Entregues', 'Lidas', 'Respondidas', 'Falhas', 'Tx. Entrega', 'Tx. Leitura', 'Tx. Resposta'];
    const rows = campaignRows.map((c) =>
      [c.name, c.sent, c.delivered, c.read, c.replied, c.failed, formatRate(c.deliveryRate), formatRate(c.readRate), formatRate(c.replyRate)]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')
    );
    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-completo-${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxDailySent = Math.max(...dailyStats.map((d) => d.sent), 1);
  const maxRevenue = Math.max(...revenueMonths.map((m) => m.revenue), 1);

  const tabs = [
    { key: 'performance', label: 'Desempenho', icon: 'ri-bar-chart-2-line' },
    { key: 'revenue', label: 'Receita', icon: 'ri-money-dollar-circle-line' },
    { key: 'clients', label: 'Clientes', icon: 'ri-user-line' },
    { key: 'conversion', label: 'Conversão', icon: 'ri-line-chart-line' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div><h2 className="text-xl font-heading font-bold text-foreground-950">Relatórios</h2></div>
        <div className="bg-white rounded-xl border border-background-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">Carregando relatórios...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-heading font-bold text-foreground-950">Relatórios</h2>
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-700 font-medium mb-1">Erro ao carregar relatórios</p>
          <p className="text-xs text-foreground-500 mb-4">{error}</p>
          <button onClick={fetchReports} className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Relatórios</h2>
          <p className="text-sm text-foreground-500 mt-1">Análise completa de desempenho, receita, clientes e conversão</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            disabled={exportLoading}
            className="px-3 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-text-line" /> CSV
          </button>
          <button
            onClick={handleExportExcel}
            className="px-3 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line" /> Excel
          </button>
        </div>
      </div>

      {exportError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line" />{exportError}
          <button onClick={() => setExportError('')} className="ml-auto text-red-400 hover:text-red-600 cursor-pointer"><i className="ri-close-line" /></button>
        </div>
      )}
      {exportSuccess && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex items-center gap-2">
          <i className="ri-check-line" />{exportSuccess}
        </div>
      )}

      {/* Tab Switcher */}
      <div className="inline-flex bg-background-100 rounded-full p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === tab.key
                ? 'bg-white text-foreground-950 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${tab.icon} text-base`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── PERFORMANCE TAB ─── */}
      {activeTab === 'performance' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Total Enviadas</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">{formatNumber(summary.totalMessages)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Taxa Entrega</p>
              <p className="text-2xl font-heading font-bold text-emerald-600">{formatRate(summary.deliveredRate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Taxa Leitura</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">{formatRate(summary.readRate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Taxa Resposta</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">{formatRate(summary.replyRate)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-background-200 p-3 text-center">
              <p className="text-xs text-foreground-500">Campanhas</p>
              <p className="text-xl font-heading font-bold text-foreground-950">{summary.totalCampaigns}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-3 text-center">
              <p className="text-xs text-foreground-500">Ativas agora</p>
              <p className="text-xl font-heading font-bold text-accent-600">{summary.activeCampaigns}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-3 text-center">
              <p className="text-xs text-foreground-500">Total mensagens</p>
              <p className="text-xl font-heading font-bold text-foreground-950">{formatNumber(summary.totalMessages)}</p>
            </div>
          </div>

          {/* Daily Chart */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">Últimos 7 Dias</h3>
            {dailyStats.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-foreground-500">Sem dados dos últimos 7 dias</p></div>
            ) : (
              <>
                <div className="flex items-end gap-3 h-[180px]">
                  {dailyStats.map((d) => {
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full flex flex-col items-center gap-0.5">
                          <span className="text-xs font-semibold text-foreground-700">{d.sent}</span>
                          <span className="text-[10px] text-foreground-400">{d.read} lidas</span>
                        </div>
                        <div className="w-full flex justify-center gap-1">
                          <div className="w-5 bg-accent-500 rounded-t-md transition-all duration-500" style={{ height: `${maxDailySent > 0 ? (d.delivered / maxDailySent) * 140 : 0}px` }} />
                          <div className="w-5 bg-primary-400/60 rounded-t-md transition-all duration-500" style={{ height: `${maxDailySent > 0 ? (d.read / maxDailySent) * 140 : 0}px` }} />
                        </div>
                        <span className="text-[11px] text-foreground-500 font-medium">{d.date}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t border-background-100">
                  <div className="flex items-center gap-2 text-xs text-foreground-500"><span className="w-3 h-3 rounded-sm bg-accent-500" /> Entregues</div>
                  <div className="flex items-center gap-2 text-xs text-foreground-500"><span className="w-3 h-3 rounded-sm bg-primary-400/60" /> Lidas</div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ─── REVENUE TAB ─── */}
      {activeTab === 'revenue' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Receita Total</p>
              <p className="text-2xl font-heading font-bold text-emerald-600">{formatCurrency(summary.totalRevenue)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Ticket Médio</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">
                {summary.totalClients > 0 ? formatCurrency(summary.totalRevenue / summary.totalClients) : 'R$ 0,00'}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Clientes Pagantes</p>
              <p className="text-2xl font-heading font-bold text-accent-600">{summary.activeClients}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">MRR Estimado</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">
                {summary.activeClients > 0 ? formatCurrency(summary.totalRevenue / Math.max(revenueMonths.filter((m) => m.revenue > 0).length, 1)) : 'R$ 0,00'}
              </p>
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">Receita - Últimos 12 Meses</h3>
            {revenueMonths.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-foreground-500">Sem dados de receita</p></div>
            ) : (
              <div className="flex items-end gap-2 h-[180px]">
                {revenueMonths.map((m) => {
                  const pct = maxRevenue > 0 ? (m.revenue / maxRevenue) * 140 : 0;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5">
                      <span className="text-[10px] font-semibold text-foreground-700">{m.revenue > 0 ? `R$${Math.round(m.revenue)}` : ''}</span>
                      <div className={`w-full rounded-t-md transition-all duration-500 ${m.revenue > 0 ? 'bg-emerald-500' : 'bg-background-200'}`} style={{ height: `${pct > 0 ? Math.max(pct, 4) : 4}px` }} />
                      <span className="text-[10px] text-foreground-400 font-medium">{m.month}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── CLIENTS TAB ─── */}
      {activeTab === 'clients' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Total Clientes</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">{summary.totalClients}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Ativos (Pagantes)</p>
              <p className="text-2xl font-heading font-bold text-emerald-600">{summary.activeClients}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Em Teste</p>
              <p className="text-2xl font-heading font-bold text-amber-600">{summary.trialClients}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Taxa Conversão</p>
              <p className="text-2xl font-heading font-bold text-accent-600">{formatRate(summary.conversionRate)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">Distribuição de Clientes</h3>
            <div className="flex items-center gap-8">
              <div className="flex-1 space-y-4">
                {[
                  { label: 'Ativos', value: summary.activeClients, total: summary.totalClients, color: 'bg-emerald-500' },
                  { label: 'Em Teste', value: summary.trialClients, total: summary.totalClients, color: 'bg-amber-500' },
                  { label: 'Inativos/Outros', value: summary.totalClients - summary.activeClients - summary.trialClients, total: summary.totalClients, color: 'bg-background-300' },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-foreground-600">{item.label}</span>
                      <span className="text-xs font-semibold text-foreground-800">{item.value}</span>
                    </div>
                    <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-700 ${item.color}`} style={{ width: `${item.total > 0 ? (item.value / item.total) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex-shrink-0 w-32 h-32 rounded-full border-4 border-background-100 flex flex-col items-center justify-center">
                <span className="text-sm font-heading font-bold text-foreground-950">{summary.totalClients}</span>
                <span className="text-[10px] text-foreground-400">Total</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── CONVERSION TAB ─── */}
      {activeTab === 'conversion' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Trial &rarr; Pago</p>
              <p className="text-2xl font-heading font-bold text-emerald-600">{formatRate(summary.conversionRate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Tx. Entrega Média</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">{formatRate(summary.deliveredRate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Tx. Leitura Média</p>
              <p className="text-2xl font-heading font-bold text-foreground-950">{formatRate(summary.readRate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 font-medium mb-1">Tx. Resposta Média</p>
              <p className="text-2xl font-heading font-bold text-accent-600">{formatRate(summary.replyRate)}</p>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">Funil de Conversão</h3>
            <div className="space-y-3">
              {[
                { label: 'Enviadas', value: summary.totalMessages, pct: 100, color: 'bg-foreground-700' },
                { label: 'Entregues', value: Math.round(summary.totalMessages * summary.deliveredRate / 100), pct: summary.deliveredRate, color: 'bg-primary-500' },
                { label: 'Lidas', value: Math.round(summary.totalMessages * summary.deliveredRate * summary.readRate / 10000), pct: Math.round(summary.deliveredRate * summary.readRate / 100), color: 'bg-accent-500' },
                { label: 'Respondidas', value: Math.round(summary.totalMessages * summary.deliveredRate * summary.replyRate / 10000), pct: Math.round(summary.deliveredRate * summary.replyRate / 100), color: 'bg-emerald-500' },
              ].map((step, i) => (
                <div key={step.label} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-foreground-600 w-20 text-right flex-shrink-0">{step.label}</span>
                  <div className="flex-1 h-7 bg-background-50 rounded-lg overflow-hidden relative">
                    <div className={`h-full ${step.color} rounded-lg transition-all duration-700 flex items-center justify-end pr-3`} style={{ width: `${step.pct}%`, opacity: 0.9 - i * 0.12 }}>
                      <span className="text-xs font-semibold text-white">{formatNumber(step.value)}</span>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-foreground-700 w-12 flex-shrink-0">{formatRate(step.pct)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Campaign Performance Table - always visible */}
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-base font-heading font-semibold text-foreground-950">Desempenho por Campanha</h3>
          <p className="text-xs text-foreground-400">Clique em uma linha para selecionar campanha para exportação</p>
        </div>
        {campaignRows.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-megaphone-line text-xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500">Nenhuma campanha encontrada</p>
            <p className="text-xs text-foreground-400 mt-1">Crie campanhas para ver os relatórios aqui</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Campanha</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Env.</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Entr.</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Lidas</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Resp.</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Falhas</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tx. E</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tx. L</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tx. R</th>
                </tr>
              </thead>
              <tbody>
                {campaignRows.map((c) => {
                  const isSelected = selectedCampaign === c.id;
                  return (
                    <tr key={c.id} onClick={() => setSelectedCampaign(isSelected ? null : c.id)} className={`border-b border-background-50 cursor-pointer transition-colors ${isSelected ? 'bg-primary-50/50' : 'hover:bg-background-50'}`}>
                      <td className="px-5 py-3">
                        <span className="font-medium text-foreground-900">{c.name}</span>
                        {isSelected && <i className="ri-check-line text-primary-500 ml-2" />}
                      </td>
                      <td className="px-3 py-3 text-center text-foreground-700">{formatNumber(c.sent)}</td>
                      <td className="px-3 py-3 text-center text-foreground-700">{formatNumber(c.delivered)}</td>
                      <td className="px-3 py-3 text-center text-foreground-700">{formatNumber(c.read)}</td>
                      <td className="px-3 py-3 text-center text-foreground-700">{formatNumber(c.replied)}</td>
                      <td className="px-3 py-3 text-center"><span className={c.failed > 50 ? 'text-red-600 font-medium' : 'text-foreground-600'}>{formatNumber(c.failed)}</span></td>
                      <td className="px-3 py-3 text-center"><span className={`font-semibold text-xs ${rateColor(c.deliveryRate)}`}>{formatRate(c.deliveryRate)}</span></td>
                      <td className="px-3 py-3 text-center"><span className={`font-semibold text-xs ${rateColor(c.readRate)}`}>{formatRate(c.readRate)}</span></td>
                      <td className="px-3 py-3 text-center"><span className={`font-semibold text-xs ${rateColor(c.replyRate)}`}>{formatRate(c.replyRate)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}